import { Link, createFileRoute } from '@tanstack/react-router'
import { ArrowRight, Plus } from 'lucide-react'
import { cx } from '@/lib/clases'
import { formatearMonto } from '@/lib/monto'
import { formatearRNC } from '@/lib/rnc'
import { Boton } from '@/ui/Boton'
import { Banner } from '@/ui/Banner'
import { EstadoError } from '@/ui/EstadoError'
import { EstadoVacio } from '@/ui/EstadoVacio'
import { Esqueleto, LineaEsqueleto } from '@/ui/Esqueleto'
import { InsigniaEstado, PuntoEstado } from '@/ui/InsigniaEstado'
import { ENCF, FechaCorta, Monto } from '@/ui/Numero'
import { PRESENTACION_ESTADO } from '@/dominio/documentos/estados'
import type { SecuenciasDelTipo } from '@/dominio/tablero/tipos'
import { useTablero } from '@/dominio/tablero/useTablero'
import { BarraConsumo, Cifra, EncabezadoPantalla, Tarjeta } from './-pantalla'
import { AvisoTssPortal } from './-nomina/Obligaciones'
import { CortesDelMes } from './-reportes/CortesDelMes'

/**
 * Tablero de inicio (E34).
 *
 * **Cuatro cifras arriba, dos paneles abajo. Nada más.** "La tentación de
 * llenarlo de gráficos es exactamente lo que hace que un producto parezca
 * generado" (`docs/07-frontend.md`).
 *
 * Y las dos reglas que lo hacen útil:
 *   **cada cifra es un enlace** al listado ya filtrado
 *   **"Requieren atención" solo muestra cosas accionables**; si no hay nada,
 *   dice "todo en orden" en dos líneas y no ocupa un vacío con una ilustración
 */
export const Route = createFileRoute('/inicio')({
  component: PantallaInicio,
})

function PantallaInicio() {
  const consulta = useTablero()
  const tablero = consulta.data

  if (consulta.isError) {
    return (
      <div className="flex flex-col gap-4">
        <EncabezadoPantalla titulo="Inicio" />
        <EstadoError
          titulo="No pudimos cargar el tablero."
          contexto="Tu trabajo no se perdió. Vuelve a intentarlo."
          onReintentar={() => void consulta.refetch()}
          detalleTecnico={consulta.error instanceof Error ? consulta.error.message : undefined}
        />
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-6">
      <EncabezadoPantalla
        titulo="Inicio"
        acciones={
          <Link to="/emitir">
            <Boton variante="primario" icono={<Plus />} atajo="N">
              Nueva factura
            </Boton>
          </Link>
        }
      />
      <AvisoTssPortal />

      {/* Cuatro cifras ------------------------------------------------- */}
      <div className="grid grid-cols-2 gap-4 xl:grid-cols-4">
        {consulta.isLoading || tablero === undefined ? (
          <>
            {[0, 1, 2, 3].map((n) => (
              <div key={n} className="flex flex-col gap-2 rounded-md border border-gris-300 bg-white p-4">
                <LineaEsqueleto ancho="w-1/3" />
                <Esqueleto className="h-7 w-2/3" />
                <LineaEsqueleto ancho="w-1/2" />
              </div>
            ))}
          </>
        ) : (
          <>
            <Cifra
              etiqueta="Hoy"
              valor={formatearMonto(tablero.hoy.cantidad, { decimales: 0 })}
              monto={tablero.hoy.monto}
              destino="/documentos"
            />
            <Cifra
              etiqueta="Pendientes"
              valor={formatearMonto(tablero.pendientes.cantidad, { decimales: 0 })}
              // "12 pendientes" a secas no sirve: hay un plazo legal corriendo.
              // El plazo lo dice el servidor en `horasDePlazo`. Estaba escrito
              // «de 72» a mano: una cifra normativa incrustada en el sitio donde
              // nadie la va a buscar el día que el instructivo la mueva.
              nota={
                tablero.pendientes.horasDelMasViejo === null
                  ? 'nada en espera'
                  : `el más viejo ${tablero.pendientes.horasDelMasViejo} h de ${tablero.pendientes.horasDePlazo}`
              }
              destino="/documentos"
              filtros={{ estado: 'en_contingencia' }}
              // Y quién se pasó del plazo también lo decide el servidor, que es
              // el que sabe en qué momento entró cada uno.
              tono={tablero.pendientes.fueraDePlazo ? 'rojo' : undefined}
            />
            <Cifra
              etiqueta="Rechazados"
              valor={formatearMonto(tablero.rechazados.cantidad, { decimales: 0 })}
              nota="sin resolver"
              destino="/documentos"
              filtros={{ estado: 'rechazado' }}
              tono={tablero.rechazados.cantidad > 0 ? 'rojo' : undefined}
            />
            <Cifra
              etiqueta="Secuencia"
              valor={formatearMonto(tablero.secuencia.disponibles, { decimales: 0 })}
              nota="disponibles"
              destino="/configuracion"
            />
          </>
        )}
      </div>

      {/* Dos paneles --------------------------------------------------- */}
      <div className="grid grid-cols-1 items-start gap-4 xl:grid-cols-[1.6fr_1fr]">
        <Tarjeta
          titulo="Últimas emitidas"
          relleno={false}
          acciones={
            <Link
              to="/documentos"
              className="inline-flex items-center gap-1 text-cuerpo text-marca hover:underline"
            >
              Ver todas
              <ArrowRight aria-hidden="true" className="size-4" />
            </Link>
          }
        >
          {consulta.isLoading || tablero === undefined ? (
            <div className="flex flex-col gap-2 p-4">
              {[0, 1, 2, 3, 4, 5, 6, 7].map((n) => (
                <LineaEsqueleto key={n} ancho={n % 2 === 0 ? 'w-full' : 'w-5/6'} />
              ))}
            </div>
          ) : tablero.ultimasEmitidas.length === 0 ? (
            <EstadoVacio
              variante="primera-vez"
              mensaje="Aún no has emitido facturas."
              accion={
                <Link to="/emitir">
                  <Boton variante="primario" icono={<Plus />}>
                    Nueva factura
                  </Boton>
                </Link>
              }
            />
          ) : (
            <div data-densidad="compacta">
              <table className="w-full table-fixed border-collapse text-cuerpo">
                <thead className="bg-gris-50">
                  <tr className="border-b border-gris-300">
                    <th scope="col" className="h-[var(--alto-fila)] w-[92px] px-2 text-right text-etiqueta font-semibold text-gris-600">
                      Fecha
                    </th>
                    <th scope="col" className="h-[var(--alto-fila)] w-[150px] px-2 text-right text-etiqueta font-semibold text-gris-600">
                      e-NCF
                    </th>
                    <th scope="col" className="h-[var(--alto-fila)] px-2 text-etiqueta font-semibold text-gris-600">
                      Cliente
                    </th>
                    <th scope="col" className="h-[var(--alto-fila)] w-[120px] px-2 text-right text-etiqueta font-semibold text-gris-600">
                      Monto
                    </th>
                    <th scope="col" className="h-[var(--alto-fila)] w-[200px] px-2 text-etiqueta font-semibold text-gris-600">
                      Estado
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {tablero.ultimasEmitidas.map((documento) => {
                    const presentacion = PRESENTACION_ESTADO[documento.estado]
                    return (
                      <tr key={documento.id} className="h-[var(--alto-fila)] border-b border-gris-200 last:border-b-0">
                        <td className="px-2 text-right">
                          <FechaCorta valor={documento.fecha} />
                        </td>
                        <td className="overflow-hidden px-2 text-right">
                          <ENCF valor={documento.eNCF} abreviado />
                        </td>
                        <td className="overflow-hidden px-2">
                          {/* La razón social puede faltar —el consumidor final
                              de un tipo 32 no la tiene—, y entonces se enseña
                              el RNC en vez de una celda en blanco. */}
                          <span
                            className="block truncate text-gris-900"
                            title={documento.contraparteNombre ?? undefined}
                          >
                            {documento.contraparteNombre ?? formatearRNC(documento.contraparteRNC)}
                          </span>
                        </td>
                        <td className="px-2 text-right">
                          <Monto valor={documento.total} moneda={documento.moneda} />
                        </td>
                        <td className="overflow-hidden px-2">
                          <InsigniaEstado
                            tono={presentacion.tono}
                            aclaracion={presentacion.aclaracion}
                            titulo={presentacion.descripcion}
                          >
                            {presentacion.etiqueta}
                          </InsigniaEstado>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Tarjeta>

        <div className="flex flex-col gap-4">
          <Tarjeta titulo="Requieren atención" relleno={false}>
            {consulta.isLoading || tablero === undefined ? (
              <div className="flex flex-col gap-2 p-4">
                <LineaEsqueleto ancho="w-3/4" />
                <LineaEsqueleto ancho="w-1/2" />
                <LineaEsqueleto ancho="w-2/3" />
              </div>
            ) : tablero.atencion.length === 0 ? (
              // Dos líneas, no un espacio vacío con una ilustración.
              <EstadoVacio variante="resuelto" mensaje="Todo en orden." />
            ) : (
              <ul className="flex flex-col">
                {tablero.atencion.map((asunto) => (
                  <li key={asunto.id} className="border-b border-gris-200 last:border-b-0">
                    <Link
                      to={asunto.destino}
                      // `filtros` llega `null` cuando el asunto no lleva
                      // ninguno, y `null` no es lo mismo que «sin filtros» para
                      // el enrutador: se normaliza aquí.
                      search={asunto.filtros ?? undefined}
                      className={cx(
                        'flex items-start gap-2 px-4 py-2',
                        'hover:bg-gris-50 transition-colors duration-[var(--duracion-corta)]',
                      )}
                    >
                      <span className="mt-[6px]">
                        <PuntoEstado tono={asunto.tono} />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-cuerpo text-gris-900">{asunto.texto}</span>
                        {asunto.plazo !== null ? (
                          <span className="block text-etiqueta text-gris-600">{asunto.plazo}</span>
                        ) : null}
                      </span>
                      <ArrowRight aria-hidden="true" className="mt-1 size-4 shrink-0 text-gris-400" />
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </Tarjeta>

          {/* Tres cortes y no uno: el 10, el 15 y el 20 (docs/03 §11). */}
          {tablero !== undefined && tablero.cortes.length > 0 ? <CortesDelMes cortes={tablero.cortes} /> : null}

          <Tarjeta titulo="Secuencias" relleno={false}>
            {consulta.isLoading || tablero === undefined ? (
              <div className="flex flex-col gap-2 p-4">
                <LineaEsqueleto ancho="w-2/3" />
                <LineaEsqueleto ancho="w-1/2" />
              </div>
            ) : (
              <EstadoDeSecuencias tipos={tablero.secuencia.tipos} />
            )}
          </Tarjeta>
        </div>
      </div>
    </div>
  )
}

/**
 * Estado de las secuencias: **alerta al 20% restante y a 60 días del
 * vencimiento**. Los dos umbrales son operativos, no normativos, y se
 * distinguen de los plazos legales, que no se tocan.
 */
function EstadoDeSecuencias({ tipos }: { tipos: readonly SecuenciasDelTipo[] }) {
  // El agrupamiento es **por tipo de e-CF y no por rango**, y eso es lo
  // correcto aunque cueste una vuelta: al contribuyente no le sirve saber que
  // el rango `3333…` va por el 85%. Le sirve saber que **se le acaban las
  // facturas de crédito fiscal**, que es la frase con la que va a pedirle otro
  // rango a DGII. Un tipo puede tener varios rangos vigentes y lo que se agota
  // es el conjunto.
  //
  // Los umbrales los decide el servidor. Esta pantalla los recalculaba con
  // `conAlertas` y la fecha del navegador: un cajero con el reloj corrido —o
  // simplemente en otro huso— veía un vencimiento distinto del que el servidor
  // calcula con el calendario dominicano.
  const criticos = tipos.filter((t) => t.porAgotarse || t.porVencer || t.sinRangoUtilizable)

  if (criticos.length === 0) {
    return <EstadoVacio variante="resuelto" mensaje="Ningún rango por agotarse ni por vencer." />
  }

  return (
    <ul className="flex flex-col">
      {criticos.map((secuencia) => (
        <li
          key={secuencia.tipo}
          className="flex flex-col gap-1 border-b border-gris-200 px-4 py-2 last:border-b-0"
        >
          <div className="flex items-baseline justify-between gap-2">
            {/* El nombre del tipo, no solo su código. «Se te acaban las
                facturas de crédito fiscal» es accionable; «el tipo 31 va al
                85%» obliga a buscar qué es el 31. */}
            <span className="text-cuerpo text-gris-900">{secuencia.nombre}</span>
            <span className="numero text-gris-700">
              {formatearMonto(secuencia.disponibles, { decimales: 0 })} disponibles
            </span>
          </div>
          <BarraConsumo porcentaje={secuencia.porcentajeConsumido} />
          <p className="text-etiqueta text-gris-600">
            {/* Sin rango utilizable va primero y solo: ya no se puede emitir de
                ese tipo, así que el porcentaje sobra. */}
            {secuencia.sinRangoUtilizable ? (
              <span className="text-rojo-700">sin rango disponible: no puedes emitir este tipo</span>
            ) : (
              <>
                {secuencia.porAgotarse ? `consumido al ${secuencia.porcentajeConsumido}%` : null}
                {secuencia.porAgotarse && secuencia.porVencer ? ' · ' : null}
                {secuencia.porVencer && secuencia.diasParaVencer !== null
                  ? `vence en ${secuencia.diasParaVencer} días`
                  : null}
              </>
            )}
          </p>
        </li>
      ))}
    </ul>
  )
}

/** Banner de bloqueo por certificado vencido. No se puede cerrar, y es correcto. */
export function BannerCertificadoVencido({ vence }: { vence: string }) {
  return (
    <Banner tono="rojo" titulo={`No puedes facturar: tu certificado venció el ${vence}`}>
      Sube el certificado vigente para volver a emitir.
    </Banner>
  )
}
